import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import { alertCaption, dashboardAlertWhen, latestAlerts, type Alert } from '../net/alerts';
import { ApiError } from '../net/client';
import { batchLine, batchTitle, recentBatches, type CodeBatch } from '../net/codeBatches';
import { listAllProducts } from '../net/products';
import {
  barTrack,
  dashboardCards,
  dashboardWindows,
  firstName,
  getDashboardSummary,
  getOrganization,
  getSummary,
  greeting,
  placeLabel,
  scansLine,
  weekBars,
  type StatCard,
  type TopPlace,
  type WeekBars,
} from '../net/reports';
import { useSession, useSessionState } from '../session/useSession';
import { colors, radius, shell, type, verdict } from '../theme';

/**
 * G1 and P2, `design/PortalDashboard.dc.html`.
 *
 * The header, the four stat cards and the four panels. Every value below is
 * literal in that board. The cards and the weeks and places panels are from
 * `GET /v1/reports/summary`; the alerts and batches panels are one page each
 * of their own routes, three rows as the board draws. What the board does not
 * draw is D9 and D39: an empty tenant is the same cards reading zero, an empty
 * panel says so in one muted line.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | {
      status: 'loaded';
      organization: string;
      cards: StatCard[];
      weeks: WeekBars[];
      places: TopPlace[];
      alerts: Alert[];
      batches: CodeBatch[];
      products: Map<string, string>;
    };

/** The board draws three rows in each list panel. */
const rows = 3;

const placeholder: StatCard[] = [
  'Total scans',
  'Genuine rate',
  'Suspect scans',
  'Active codes',
].map((label) => ({
  label,
  value: '',
  note: '',
  tone: 'muted',
}));

/** A no-break space: an empty line at the step's own height. */
const keepLine = '\u00a0';

const tones: Record<StatCard['tone'], string> = {
  genuine: verdict.genuine.fg,
  muted: colors.mutedInk,
  suspect: verdict.suspect.fg,
};

export function Dashboard() {
  const { api } = useSession();
  const state = useSessionState();
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const windows = dashboardWindows(new Date());
    Promise.all([
      getOrganization(api, signal),
      getDashboardSummary(api, windows.current, signal),
      getSummary(api, windows.previous, signal),
      getSummary(api, windows.week, signal),
      latestAlerts(api, rows, signal),
      recentBatches(api, rows, signal),
      listAllProducts(api, signal),
    ]).then(
      ([organization, current, previous, week, alerts, batches, products]) =>
        setLoad({
          status: 'loaded',
          organization: organization.name,
          cards: dashboardCards(current, previous, week),
          weeks: weekBars(current.weeks),
          places: current.places.top.slice(0, rows),
          alerts,
          batches,
          products: new Map(products.map((p) => [p.id, p.name])),
        }),
      (error: unknown) => {
        if (!signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api]);

  // The board draws the morning string; the hour picks the word, D39.
  const name = state.status === 'signed_in' ? firstName(state.user.name) : '';
  const hello = greeting(new Date());
  const title = name === '' ? hello : `${hello}, ${name}`;
  const now = new Date();

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading your dashboard</Waiting> : null}
      <PageHeader
        title={title}
        {...(load.status === 'loaded'
          ? { subtitle: `${load.organization} · last 30 days at a glance` }
          : {})}
        action={
          <button
            className="vp-fill"
            type="button"
            onClick={() => navigate('/code-batches')}
            style={issueButton}
          >
            Issue a batch
          </button>
        }
      />
      {load.status === 'failed' ? (
        <Card title="The dashboard could not be loaded" body={failureText(load.error)} />
      ) : (
        <div
          role="list"
          aria-label="Last 30 days"
          aria-busy={load.status === 'loading'}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}
        >
          {(load.status === 'loaded' ? load.cards : placeholder).map((card) => (
            <div key={card.label} role="listitem" aria-label={card.label} style={statCard}>
              <div style={{ ...type.statLabel, color: colors.mutedInk }}>{card.label}</div>
              {/* A withheld figure keeps its line, so the card does not collapse. */}
              <div style={{ ...type.statValue, color: colors.navy }}>{card.value || keepLine}</div>
              <div style={{ ...type.captionStrong, color: tones[card.tone] }}>
                {card.note || keepLine}
              </div>
            </div>
          ))}
        </div>
      )}
      {load.status === 'loaded' ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, flexGrow: 1 }}>
            <WeeksPanel weeks={load.weeks} />
            <section aria-label="Latest alerts" style={panel}>
              <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 16px 10px 16px',
                  }}
                >
                  <h2 style={panelTitle}>Latest alerts</h2>
                  <Link
                    className="vp-link"
                    to="/alerts"
                    style={{ ...type.metaStrong, color: colors.brand, textDecoration: 'none' }}
                  >
                    View all
                  </Link>
                </div>
                {load.alerts.length === 0 ? (
                  <div style={{ ...empty, padding: '13px 16px' }}>No alerts yet</div>
                ) : (
                  <ul aria-label="Latest alerts" style={list}>
                    {load.alerts.map((alert, i) => (
                      <AlertRow
                        key={alert.id}
                        alert={alert}
                        product={alert.productId && load.products.get(alert.productId)}
                        when={dashboardAlertWhen(alert, now)}
                        divided={i > 0}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ListPanel
              title="Top scan locations"
              emptyText="No located scans yet"
              items={load.places.map((top) => ({
                key: `${top.lat},${top.lng}`,
                name: placeLabel(top),
                detail: (
                  <>
                    {`${scansLine(top.total)} · `}
                    <span style={{ color: verdict.suspect.fg, fontWeight: 700 }}>
                      {`${top.suspect.toLocaleString('en-US')} suspect`}
                    </span>
                  </>
                ),
              }))}
            />
            <ListPanel
              title="Recent code batches"
              emptyText="No batches yet"
              items={load.batches.map((batch) => ({
                key: batch.id,
                name: batchTitle(batch, load.products.get(batch.productId)),
                detail: batchLine(batch, now),
              }))}
            />
          </div>
        </>
      ) : null}
    </>
  );
}

/** "Scans · last 8 weeks". A week before the first scan draws its label and no bar. */
function WeeksPanel({ weeks }: { weeks: WeekBars[] }) {
  return (
    <section aria-label="Scans, last 8 weeks" style={panel}>
      <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h2 style={panelTitle}>Scans · last 8 weeks</h2>
        <ol
          aria-label="Weeks"
          style={{
            ...list,
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: 22,
            height: 170,
            padding: '0 6px',
          }}
        >
          {weeks.map((week) => (
            <li
              key={week.label}
              aria-label={weekName(week)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                // A zero basis: a week with no bar keeps its share of the row.
                flex: '1 1 0',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: barTrack.height }}
              >
                {week.total === null ? null : (
                  <>
                    <div
                      data-part="total"
                      style={{
                        width: 22,
                        height: week.totalHeight,
                        borderRadius: '6px 6px 3px 3px',
                        background: colors.navy,
                      }}
                    />
                    <div
                      data-part="suspect"
                      style={{
                        width: 10,
                        height: week.suspectHeight,
                        borderRadius: '4px 4px 2px 2px',
                        background: verdict.suspect.fg,
                      }}
                    />
                  </>
                )}
              </div>
              <div style={{ fontSize: 11, color: colors.mutedInk }}>{week.label}</div>
            </li>
          ))}
        </ol>
        <div style={{ display: 'flex', gap: 20, padding: '4px 6px 0 6px' }}>
          <Key colour={colors.navy}>Scans</Key>
          <Key colour={verdict.suspect.fg}>Suspect scans</Key>
        </div>
      </div>
    </section>
  );
}

function weekName(week: WeekBars): string {
  if (week.total === null) return `${week.label}, before your first scan`;
  return `${week.label}, ${scansLine(week.total)}, ${week.suspect ?? 0} suspect`;
}

function Key({ colour, children }: { colour: string; children: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <div style={{ width: 12, height: 12, borderRadius: 3, background: colour }} />
      <div style={{ ...type.caption, color: colors.mutedInk }}>{children}</div>
    </div>
  );
}

function AlertRow({
  alert,
  product,
  when,
  divided,
}: {
  alert: Alert;
  product: string | undefined | '';
  when: string;
  divided: boolean;
}) {
  return (
    <li
      aria-label={alert.title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 16px',
        borderTop: divided ? `1px solid ${colors.line}` : 'none',
      }}
    >
      <div
        data-part="severity"
        style={{
          width: 8,
          height: 36,
          borderRadius: 4,
          background: alert.severity === 'critical' ? verdict.unknown.fg : verdict.suspect.fg,
          flexShrink: 0,
        }}
      />
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <div style={{ ...type.bodyStrong, color: colors.navy }}>{alert.title}</div>
        <div style={{ ...type.caption, color: colors.mutedInk }}>
          {alertCaption(alert, product, when)}
        </div>
      </div>
      {alert.readAt === undefined ? (
        <div
          role="img"
          aria-label="Unread"
          style={{ width: 8, height: 8, borderRadius: 4, background: colors.brand, flexShrink: 0 }}
        />
      ) : null}
    </li>
  );
}

function ListPanel({
  title,
  emptyText,
  items,
}: {
  title: string;
  emptyText: string;
  items: { key: string; name: string; detail: ReactNode }[];
}) {
  return (
    <section aria-label={title} style={panel}>
      <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h2 style={panelTitle}>{title}</h2>
        {items.length === 0 ? (
          <div style={empty}>{emptyText}</div>
        ) : (
          <ul aria-label={title} style={{ ...list, gap: 8 }}>
            {items.map((item) => (
              <li
                key={item.key}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span style={{ ...type.bodyStrong, color: colors.navy }}>{item.name}</span>
                <span style={{ ...type.meta, color: colors.mutedInk }}>{item.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

const statCard: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
  padding: '18px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const panel: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

const panelTitle: CSSProperties = { ...type.panelTitle, color: colors.navy, margin: 0 };

const list: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
};

/** The empty panel's one line, D39. */
const empty: CSSProperties = { ...type.caption, color: colors.mutedInk };

const issueButton: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  background: colors.brand,
  color: colors.white,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};
